import { Field, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'

import { parseClasses } from '../utils'
import CustomForm from './common/forms/CustomForm'
import TextInput from './common/forms/inputs/Text'
import { Button } from './ui/Button'
import { Scrollbar } from './ui/Scrollbars'
import FieldGroup from './common/forms/FieldGroup'
import Meta from './common/datatable/Meta'
import DataTable from './common/datatable/DataTable'


const ModelSelectSidebar = ({
  user,
  match,
  config,
  tableconfig,
  modelname,
  model,
  cache,
  filterable,
  params: fieldparams,
  addAll,
  addSelected,
  actions
}) => {
  const excluded_fields = [
    'consent__consent_messages',
    'consent__consent_emails',
    'consent__consent_calls',
    'consent__consent_information'
  ]

  const mergeparams = () => {
    const { advsearch } = config
    const mergedparams = {}
    advsearch.map(group => {
      group.filter(field => !excluded_fields.includes(field.name)).forEach(field => {
        if (field.name in fieldparams && !field.verb) {
          // Check for 1:1 match
          mergedparams[field.name] = fieldparams[field.name]
        } else if (field.verb) {
          // Check if verbed name exists in params
          const verbedname = `${field.name}${field.verb ? `__${field.verb}` : ''}`
          if (verbedname in fieldparams) {
            mergedparams[verbedname] = fieldparams[verbedname]
          } else if (field.name in fieldparams) {
            mergedparams[verbedname] = [ fieldparams[field.name] ]
          } else if (field.name === 'agents' && 'agent' in fieldparams) {
            mergedparams[verbedname] = [ fieldparams.agent ]
          } else if (field.name === 'branches' && 'branch' in fieldparams) {
            mergedparams[verbedname] = [ fieldparams.branch ]
          }
        }
      })
    })
    return mergedparams
  }

  const [ results, setResults ] = useState([])
  const [ params, setParams ] = useState(mergeparams())
  const [ initparams ] = useState(mergeparams())
  const form = useRef(null)
  const [ selected, setSelected ] = useState([])

  actions.selectOne = obj => {
    let newselected = [ ...selected ]
    if (newselected.includes(obj.id)) {
      newselected = newselected.filter(a => a !== obj.id)
    } else {
      newselected.push(obj.id)
    }
    setSelected(newselected)
  }

  actions.selectAll = () => {
    const newselected = []
    results.forEach(obj => newselected.push(obj.id))
    setSelected(newselected)
  }

  actions.selectNone = () => setSelected([])

  const initValues = () => {
    const values = {
      modelname: config.modelname,
      endpoint: config.endpoint,
      params: { ...initparams, order_by: '-created' },
      conflicts: true,
      modellist: true
    }
    new Promise((resolve, reject) => {
      actions.fetchMany({ values, resolve, reject })
    }).then(r => {
      setResults(r)
    })
  }

  useEffect(() => {
    initValues()
    return () => {
      actions.toggleWideSidebar()
    }
  }, [])

  const updateFetchParams = p => {
    const values = {
      modelname: config.modelname,
      endpoint: config.endpoint,
      params: { ...params, ...p },
      conflicts: true,
      modellist: true
    }
    new Promise((resolve, reject) => {
      actions.fetchMany({ values, resolve, reject })
    }).then(r => {
      setResults(r)
      setParams({ ...params, ...p })
    })
  }

  const handleSubmit = values => {
    setResults([])
    const vals = {
      modelname,
      conflicts: true,
      term: values.term,
      params: { ...params, ...values },
      modellist: true
    }
    new Promise((resolve, reject) => {
      actions.fetchMany({ values: vals, resolve, reject })
    }).then(r => {
      setResults(r)
      form.current?.setSubmitting(false)
      setParams({ ...params, ...values })
    }).catch(() => {
      form.current?.setSubmitting(false)
    })
  }

  const resetPage = step => { // Reset page to initial
    const hdrs = document.querySelectorAll('.orderable')
    if (!hdrs) { return }
    for (const hdr of hdrs) { hdr.classList.remove('orderedby', 'reversed') }
    if (step) { hdrs[0].classList.add('orderedby') }
  }

  const handleReset = () => { // Handles reset of the advanced search form
    resetPage(true)
    initValues()
  }

  const addVerb = () => {
    const { advsearch } = config
    if (advsearch) {
      return (
        <div className="search-fields">
          {advsearch.map((group, gidx) => {
            const fields = group.filter(field => !excluded_fields.includes(field.name)).map(field => {
              const fieldProps = config.fields.find(f => f.name === field.name)
              let classes = field.classes ? parseClasses(field.classes, form.values) : ''
              classes = classes.split(' ')
              let cols = classes.find(c => c.includes('col-'))
              classes = classes.filter(c => !c.includes('col-'))
              cols = cols.replace('col-', '')
              const verbedname = `${field.name}${field.verb ? `__${field.verb}` : ''}`
              const verbprops = {
                ...fieldProps,
                ...field,
                group: `group-${gidx}`,
                name: verbedname,
                classes,
                required: false,
                readOnly: false,
                quality: false,
                cols,
                match: match,
                id: field.id ? `field-${field.id}-${field.name}` : `field-${field.name}`
              }
              if (fieldparams && field.name in fieldparams) {
                verbprops.disabled = true
                verbprops.defaultvalue = fieldparams[field.name]
              }
              if (field.name === 'agents') {
                verbprops.extraparams += '&active=1'
                if (fieldparams && 'agent' in fieldparams) {
                  verbprops.disabled = true
                }
              }
              if (fieldparams && field.verb && verbedname in fieldparams) {
                verbprops.disabled = true
                verbprops.defaultvalue = fieldparams[verbedname]
              }
              if (verbprops.input?.endsWith('Notes')) {
                verbprops.input = verbprops.input.replace('Notes', '')
              }
              if (verbprops.input === 'Extras') {
                verbprops.hidelegend = true
              }
              return verbprops
            })
            return (
              <FieldGroup
                card={false}
                groupname={`group-${gidx}`}
                key={`group-${gidx}`}
                match={match}
                config={{
                  fields
                }}
                fields={fields}
                render={({ renderFieldGroup, hidden }) => {
                  if (hidden) { return null }
                  return renderFieldGroup({
                    fields
                  })
                }}
              />
            )
          })}
        </div>
      )
    }
    return null
  }

  return (
    <div id="model-selector-sidebar" ref={el => el} className="wide-sidebar model-selector-sidebar">
      <div className="wide-sidebar-container">
        <Formik
          initialValues={params}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={handleSubmit}
          enableReinitialize={false}
        >{ formik => {
            form.current = formik
            return (
              <CustomForm
                component="div"
                render={() => (
                  <div className="wide-sidebar-pane">
                    <div className="wide-sidebar-heading">
                      <h4>Search {config.plural}</h4>
                      <Button
                        type="button"
                        icon="#icon24-X-Large"
                        className="btn btn-none btn-icon btn-icon-24 btn-wide-sidebar-close"
                        onClick={() => { actions.toggleWideSidebar() }}
                      />
                    </div>
                    <Scrollbar
                      style={{ height: 'calc(100vh - 218px)' }}
                      renderView={({ style, ...rest }) => <div {...rest} style={{ ...style, position: 'relative', height: 'calc(100% + 15px)' }} className="scrollview"/>}
                    >
                      <div className="wide-sidebar-content">
                        <Field
                          component={TextInput}
                          name="term"
                          label="Search"
                          _value={formik.values.term}
                          defaultValue={formik.initialValues.term}
                          id="term"
                          type="text"
                        />

                        {config ? addVerb(formik) : null}

                        <div className="search-buttons">

                          <Button
                            type="button"
                            disabled={!Object.keys(formik.touched).length}
                            onClick={() => {
                              formik.resetForm()
                              handleReset()
                              setSelected([])
                            }}
                            title="Reset filters" icon="#icon16-Refresh" className="btn btn-primary btn-icon-16 btn-icon-left"
                          >
                            Reset Filters
                          </Button>

                          <Button
                            icon="#icon16-Search"
                            type="button"
                            disabled={formik.isSubmitting || !formik.dirty}
                            className="btn btn-primary btn-icon-16 btn-icon-left"
                            onClick={formik.handleSubmit}
                          >Search</Button>

                        </div>

                        {config && model && params ? (
                          <Meta
                            modelname={config.modelname}
                            toggleManager={actions.toggleManager}
                            model={{ ...model, params }}
                            match={match}
                            config={config}
                            params={params}
                            count={(model) ? model.count : 0}
                            previous={(model) ? model.previous : null}
                            next={(model) ? model.next : null}
                            advanced={true}
                            user={user}
                            cache={cache}
                            fetchMany={actions.fetchMany}
                            findStatus={() => config.statusField}
                            findDomain={() => null}
                            selectedRecords={selected}
                            query={handleSubmit}
                          />
                        ) : null }

                        {(results && results.length) ? (
                          <>
                            <div><h3>Results</h3></div>
                            <fieldset className="editgroup">
                              <DataTable
                                actions={actions}
                                model={model}
                                cache={cache[config.modelname]}
                                currency={cache.settings[user.agent.site.id].default_currency}
                                region={cache.settings[user.agent.site.id].region}
                                portals={cache.portals}
                                match={match}
                                config={config}
                                tableconfig={tableconfig}
                                selected={selected}
                                resetPage={resetPage}
                                selectable={true}
                                sidebar={true}
                                meta={true}
                                user={{
                                  permissions: user.permissions,
                                  agent: {
                                    id: user.agent.id,
                                    branches: user.agent.branches,
                                    site: user.agent.site
                                  }
                                }}
                                domains={null}
                                redirectSchema={null}
                                advanced={true}
                                findStatus={() => config.statusField}
                                findDomain={() => null}
                                updateFetchParams={updateFetchParams}
                              />
                            </fieldset>
                            <div className="search-buttons">
                              <Button className="btn btn-primary" type='button' onClick={() => {
                                addSelected(modelname, selected)
                              }}>Add Selected</Button>
                              {filterable ? (
                                <Button className="btn btn-primary" type='button' onClick={() => addAll(modelname, params)}>Add All</Button>
                              ) : null }
                            </div>
                          </>
                        ) : null }
                      </div>
                    </Scrollbar>
                  </div>
                )}
              />
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

ModelSelectSidebar.propTypes = {
  filterable: PropTypes.bool,
  config: PropTypes.object,
  configs: PropTypes.object,
  model: PropTypes.object,
  location: PropTypes.object,
  limit: PropTypes.number,
  field: PropTypes.object,
  siblingform: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
  autofill: PropTypes.object,
  tableconfig: PropTypes.object,
  meta_cache: PropTypes.object,
  match: PropTypes.object,
  user: PropTypes.object,
  cache: PropTypes.object,
  sidebar: PropTypes.string,
  label: PropTypes.string,
  params: PropTypes.object,
  toggleLookup: PropTypes.func,
  modelname: PropTypes.string,
  selected: PropTypes.array,
  addSelected: PropTypes.func,
  addAll: PropTypes.func
}

export default ModelSelectSidebar
